<template>
  <div
    v-if="projectSites && participantData"
    :class="$style.projectCriteriaEditContainer"
  >
    <div :class="$style.title">
      <b class="is-uppercase">Project Administrator</b>
    </div>
    <div
      :class="[
        $style.adjustMarginsCard,
        'flex',
        'flex-row',
        'list-item',
        'mb1',
      ]"
    >
      <div class="flex cell flex-column">
        <b class="text-size text-color padding-bottom8 bold">
          {{ projectOwnerEmail }}
        </b>
        <p class="mbx">Project Administrator</p>
      </div>
      <div v-if="!disableChangeProjectOwner">
        <a
          @click="handleChangeProjectOwner()"
          :class="$style.editBtn"
          v-if="showChangeProjectOwner"
        >
          <b-icon icon="pencil" size="is-small" />
        </a>
      </div>
      <div v-if="disableChangeProjectOwner">
        <b-tooltip
          label="More than one participant is required to change the project administrator."
          position="is-bottom"
        >
          <b-icon
            icon="pencil"
            size="is-small"
            :class="$style.disableEditIcon"
          />
        </b-tooltip>
      </div>
    </div>
    <div :class="$style.title">
      <b class="is-uppercase">Participant(s)</b>
    </div>
    <div
      v-for="item in participantData"
      :key="participantKey(item)"
      :class="['flex', 'flex-row', 'list-item', 'mb1']"
    >
      <div class="flex cell flex-column">
        <b class="text-size text-color padding-bottom8 bold">
          {{ getParticipantName(item) }}
        </b>
        <p class="mbx">{{ item.participantRole }}</p>
        <p>
          <span>
            {{ item.participantSites.length }}
            Selected Sites
          </span>
        </p>
      </div>
      <div v-if="isAllowedToModified(item)">
        <a @click="handleEdit(item)" :class="$style.editBtn">
          <b-icon icon="pencil" size="is-small" />
        </a>
        <a @click="handleDelete(item)" :class="$style.deleteBtn">
          <b-icon icon="delete" size="is-small" />
        </a>
      </div>
    </div>
    <Card
      v-if="participantData.length === 0"
      :shadowless="true"
      :class="$style.noItem"
    >
      Please invite participants
    </Card>

    <a v-if="editable" :class="$style.addParticipant" @click="handleCreate">
      + Add Participant
    </a>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import {
  ChangeProjectOwnerDTO,
  DeleteProjectParticipantPayload,
  ParticipantListDTO,
  ParticipantSitesDTO,
  ParticipationLevelEnum,
  UserEntity,
} from '@/store/modules/projects/types/projects.types';
import ParticipantForm from '../components/ParticipantForm.vue';
import { RootState } from '@/store/store';
import { ToastProgrammatic as Toast } from 'buefy';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { ProjectSite } from '../../../store/modules/projects/types/projects.types';
import Card from '@/components/Card.vue';
import { useUserEmail } from '@/utils/user.util';
import ProjectOwnerRequestForm from '../components/ProjectOwnerRequestForm.vue';
import { ApiState } from '../../../store/types/general.types';
import { getJBIUsername } from '@/jbi-shared/util/user.utils';
import { PacesParticipantRoles } from '@/jbi-shared/types/paces-project.types';
import DeleteModalBox from '@/components/DeleteModalBox.vue';

@Component({
  components: { Card },
})
export default class ParticipantTab extends Vue {
  @Prop({ default: true }) public editable!: boolean;
  @Prop() public projectOwner!: UserEntity;

  public showChangeProjectOwner: boolean = false;
  public disableChangeProjectOwner: boolean = false;

  @State((state) => state.projects.projectSites)
  public projectSites!: ProjectSite[];

  @State((state: RootState) => state.projects.participants)
  public participantList!: ParticipantListDTO[];

  @State((state: RootState) => state.projects.apiState.deleteProjectParticipant)
  public deleteProjectParticipantState!: ApiState;

  @State((state: RootState) => state.projects.apiState.createProjectParticipant)
  public createProjectParticipantState!: ApiState;

  @State((state: RootState) => state.projects.apiState.updateProjectParticipant)
  public updateProjectParticipantState!: ApiState;

  @Action('projects/getProjectSites')
  public getProjectSites!: (projectId: number) => void;

  @Action('projects/getProjectParticipants')
  public getProjectParticipants!: (payload: {
    projectId: number;
    includeInvites: boolean;
  }) => void;

  @Action('projects/getProjectDetail')
  public getProjectDetail!: (payload: { projectId: number }) => void;

  @Action('projects/deleteProjectParticipant')
  public deleteProjectParticipant!: (
    payload: DeleteProjectParticipantPayload,
  ) => void;

  get projectId() {
    return +this.$route.params.projectId;
  }

  get sites() {
    if (this.projectSites) {
      return this.projectSites.map((site) => {
        return {
          id: site.site.id,
          name: site.site.name,
          address: site.site.address,
          description: site.site.description,
        };
      });
    }

    return [];
  }

  public isAllowedToModified(participant: ParticipantListDTO): boolean {
    /** Only owner can edit or delete coordinators
     * if participant role is project coOrdinator,
     * then check if reqUser is project owner.
     *
     * else return true, as it's data collector role,
     * and it can be edit or delete by owners and coordinators.
     */

    const isAllowedToModifiedParticipant =
      participant.participantRole === PacesParticipantRoles.ProjectCoordinator
        ? this.projectOwner.email === this.userEmail
        : true;

    // If participant is NOT project owner or current user, allowed to edit or delete participant.
    return (
      this.editable &&
      isAllowedToModifiedParticipant &&
      this.projectOwner.email !== participant.userEmail &&
      this.userEmail !== participant.userEmail
    );
  }

  public mounted() {
    this.getProjectParticipants({
      projectId: this.projectId,
      includeInvites: true,
    });
    this.getProjectSites(this.projectId);
  }

  get participantData(): ParticipantListDTO[] {
    if (!this.participantList) {
      this.disableChangeProjectOwner = false;
      return [];
    }

    const participants: ParticipantListDTO[] = this.participantList.filter(
      (participant) =>
        participant.participantRole !==
        PacesParticipantRoles.ProjectAdministrator,
    );

    /** Valid participants as invite accepted Participants */
    const haveValidParticipants: boolean = participants.some(
      (participant: ParticipantListDTO) =>
        participant.participantSites.some(
          (ps: ParticipantSitesDTO) => !ps.invitationToken,
        ),
    );

    /** If no valid participants(haveValidParticipants), disable change project owner option */
    this.disableChangeProjectOwner = !haveValidParticipants;

    return participants;
  }

  get projectOwnerEmail() {
    if (this.projectOwner.email === this.userEmail) {
      this.showChangeProjectOwner = true;
      return 'You';
    } else {
      return getJBIUsername(
        this.projectOwner.email,
        this.projectOwner.oicPayload,
      );
    }
  }

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  /**
   * As we may not have id for each participant.
   * As user could be invited participant OR invite accepted participant
   * using math random to generate UniqueKey for each participant/user of list
   */
  public participantKey(item: ParticipantListDTO): string {
    return (
      item.participantRole + item.userEmail + Math.floor(Math.random() * 999)
    );
  }

  public getParticipantName(item: ParticipantListDTO) {
    if (item.userEmail === this.userEmail) {
      return 'You';
    } else if (item.participant) {
      return getJBIUsername(
        item.participant.user.email,
        item.participant.user.oicPayload,
      );
    } else {
      return item.userEmail;
    }
  }

  public handleEdit(participant: ParticipantListDTO) {
    const modal = this.$buefy.modal.open({
      parent: this,
      props: {
        isEdit: true,
        participant,
        sites: this.sites || [],
        projectOwnerEmail: this.projectOwner.email,
        participantsList: this.participantData,
        currentUserEmail: this.userEmail,
      },
      component: ParticipantForm,
      hasModalCard: true,
      events: {
        close: () => {
          modal.close();
        },
      },
    });
  }
  public handleCreate() {
    const modal = this.$buefy.modal.open({
      parent: this,
      props: {
        isEdit: false,
        participant: null,
        sites: this.sites || [],
        projectOwnerEmail: this.projectOwner.email,
        participantsList: this.participantData,
        currentUserEmail: this.userEmail,
      },
      component: ParticipantForm,
      hasModalCard: true,
      events: {
        close: () => {
          modal.close();
        },
      },
    });
  }

  public handleDelete(participant: ParticipantListDTO) {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: `Delete ${participant.userEmail}?`,
        modalContent: `${participant.userEmail} will no longer be able to access to this project and its data collection period.`,
      },
      events: {
        confirm: () => {
          // These are sites that needs to be deleted as a part of deleting the participant
          const siteIds: number[] = participant.participantSites.map(
            (participantSite: ParticipantSitesDTO) => participantSite.site,
          );
          this.deleteProjectParticipant({
            email: participant.userEmail,
            assignedRole: participant.participantRole,
            assignedSiteIds: siteIds,
            level: ParticipationLevelEnum.Project,
            projectId: this.projectId,
          });
        },
      },
    });
  }

  public handleChangeProjectOwner() {
    this.$buefy.modal.open({
      parent: this,
      props: {
        projectId: this.projectId,
        projectOwnerEmail: this.projectOwner.email,
        projectOwnerName: getJBIUsername(
          this.projectOwner.email,
          this.projectOwner.oicPayload,
        ),
        isSystemAdmin: false,
      },
      events: {
        confirm: (newValue: ChangeProjectOwnerDTO) => {
          Toast.open({
            message: `Project administration request successfully sent to ${newValue.newRequestedProjectOwnerEmail}`,
            position: 'is-top',
            type: 'is-dark',
            duration: 3500,
          });
        },
      },
      component: ProjectOwnerRequestForm,
      hasModalCard: true,
    });
  }

  @Watch('deleteProjectParticipantState.success')
  @isTruthy
  public onDeleteSuccess() {
    this.getProjectParticipants({
      projectId: this.projectId,
      includeInvites: true,
    });

    Toast.open({
      queue: true,
      type: 'is-dark',
      position: 'is-top',
      message: `Participant removed`,
    });
  }

  @Watch('createProjectParticipantState.success')
  @isTruthy
  public onCreateSuccess(): void {
    this.getProjectParticipants({
      projectId: this.projectId,
      includeInvites: true,
    });
    Toast.open({
      queue: true,
      type: 'is-dark',
      position: 'is-top',
      message: `Participant invited successfully`,
    });
  }

  @Watch('updateProjectParticipantState.success')
  @isTruthy
  public updateSuccess(): void {
    this.getProjectParticipants({
      projectId: this.projectId,
      includeInvites: true,
    });
    Toast.open({
      queue: true,
      type: 'is-dark',
      position: 'is-top',
      message: `Changes saved`,
    });
  }
}
</script>

<style lang="scss" module>
.projectCriteriaEditContainer {
  padding: 0rem 11rem;
}

.head {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin: 1rem 0;
}

.tabContainer {
  :global(.tabs > ul > li > a) {
    padding: 1.1875rem 1.25rem !important;
  }

  :global(.tab-content) {
    padding: 1rem 0;
  }
}

.noItem {
  align-items: center;
  justify-content: center;
  color: $grey;
  background: white;
  margin-bottom: 10px;
  margin-top: 15px;
}

.editBtn {
  color: #21b5ea;
  padding-right: 12px;
}

.deleteBtn {
  color: #21b5ea;
}

.title {
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
  padding: 8px 0;
}

.addParticipant {
  color: #00205b;
  font-weight: bold;
  padding-top: 10px;
  font-size: 14px;
}

.adjustMarginsCard {
  div > p {
    margin-bottom: unset;
  }
}

.disableEditIcon {
  color: #9bdbf3;
  padding-right: 12px;
}

.tooltip {
  font-family: Oswald;
  font-size: 20px;

  &::after {
    font-family: 'Open Sans';
  }
}
</style>
